import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { REVOLUT_PAYMENTS_STATE_PREFIX, type CompleteRevolutConnectionRequest } from "@ravnar/api-contract";

import { useAskAgain } from "../../../api/refresh";
import { completeRevolutConnection, completeRevolutPayments } from "../../../api/revolut";
import { PageHeader } from "../../../components/shell/app-shell";
import { buttonClassName } from "../../../components/ui/button-styles";
import { Card } from "../../../components/ui/card";
import { Skeleton } from "../../../components/ui/skeleton";
import { showToast } from "../../../lib/toast";

/**
 * Where Revolut sends the browser back once an owner or admin has approved — or declined — Ravnar's access, or its
 * payments: the one redirect URL registered for the app in Revolut's developer portal. The code and the state come in
 * the URL's fragment, which the browser never sends to a server, so this page reads them and hands them to the API —
 * to allow payments when the state says it is theirs (`REVOLUT_PAYMENTS_STATE_PREFIX`), or else to connect.
 */
export const Route = createFileRoute("/settings/revolut/callback")({
	head: () => ({ meta: [{ title: "Connecting Revolut · Ravnar" }] }),
	staticData: { crumbs: ["Settings", "Revolut"] },
	component: RevolutCallbackPage,
});

/** What Revolut sent the browser back with, and whether it was for payments. */
type Returned = { readonly forPayments: boolean } & (
	| { readonly kind: "approved"; readonly code: string; readonly state: string }
	| { readonly kind: "not-approved"; readonly message: string }
);

/**
 * What Revolut sent back: in the fragment, as Ravnar asks for it (`response_mode=fragment`), or in the query should
 * Revolut use that. What Revolut says about a refusal is not shown: it is text anyone could put in a link.
 */
function returnedFrom(hash: string, searchStr: string): Returned {
	const fromFragment = new URLSearchParams(hash);
	const returned =
		fromFragment.has("code") || fromFragment.has("error") ? fromFragment : new URLSearchParams(searchStr);
	const code = returned.get("code");
	const state = returned.get("state");
	const forPayments = state?.startsWith(REVOLUT_PAYMENTS_STATE_PREFIX) ?? false;
	if (code !== null && code !== "" && state !== null && state !== "") {
		return { forPayments, kind: "approved", code, state };
	}
	const denied = returned.get("error") === "access_denied";
	return {
		forPayments,
		kind: "not-approved",
		message: forPayments
			? denied
				? "Payments were not allowed in Revolut. Allow them again when you are ready."
				: "Revolut did not send an approval back. Allow payments again."
			: denied
				? "Ravnar's access was not approved in Revolut. Connect again when you are ready."
				: "Revolut did not send an approval back. Connect again.",
	};
}

/** Hands what came back to the API: to allow payments, or to connect. */
async function complete(request: CompleteRevolutConnectionRequest, forPayments: boolean): Promise<void> {
	await (forPayments ? completeRevolutPayments(request) : completeRevolutConnection(request));
}

function RevolutCallbackPage() {
	const { hash, searchStr } = useLocation();
	// Read once: the address changes when the page moves on, and what came back is spent by then.
	const [returned] = useState(() => returnedFrom(hash, searchStr));
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const askAgain = useAskAgain();
	const { mutateAsync, isError, error } = useMutation({
		mutationFn: (request: CompleteRevolutConnectionRequest) => complete(request, returned.forPayments),
		// The settings read the connection and the payments, and the audit entry arrives through a queue a moment after
		// this answer.
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({ queryKey: ["settings", "revolut"] }),
				queryClient.invalidateQueries({ queryKey: ["revolut"] }),
				askAgain(["settings", "audit-log"]),
			]),
	});
	// A code works once, so it is sent once: StrictMode runs this effect twice in development.
	const sentRef = useRef(false);

	useEffect(() => {
		if (sentRef.current || returned.kind !== "approved") {
			return;
		}
		sentRef.current = true;
		const finish = async () => {
			try {
				await mutateAsync({ code: returned.code, state: returned.state });
			} catch {
				// The alert below shows the error.
				return;
			}
			showToast(returned.forPayments ? "Revolut payments allowed." : "Revolut connected.");
			// Replace this entry, so Back never returns to a code that has been spent.
			await navigate({ to: "/settings/revolut", replace: true });
		};
		void finish();
	}, [returned, mutateAsync, navigate]);

	const problem = returned.kind === "not-approved" ? returned.message : isError ? error.message : undefined;
	return (
		<>
			<PageHeader
				title="Revolut"
				description={
					returned.forPayments
						? "Finishing the payments you allowed in Revolut."
						: "Finishing the connection you approved in Revolut."
				}
			/>
			<Card>
				{problem === undefined ? (
					<div
						role="status"
						aria-label={returned.forPayments ? "Allowing Revolut payments" : "Connecting Revolut"}
						className="flex flex-col gap-3 p-4"
					>
						<p className="text-body text-ink-2">
							{returned.forPayments ? "Allowing Revolut payments…" : "Connecting Revolut…"}
						</p>
						<Skeleton className="h-3.5 w-2/3" />
						<Skeleton className="h-3.5 w-1/2" />
					</div>
				) : (
					<div className="flex flex-col items-start gap-3 p-4">
						<p role="alert" className="text-caption text-alert">
							{problem}
						</p>
						<Link to="/settings/revolut" className={buttonClassName()}>
							Back to Revolut
						</Link>
					</div>
				)}
			</Card>
		</>
	);
}
